'use client';

import {useEffect, useRef, useState} from 'react';
import type {InterviewProcessStep} from '../types';

export function StagesTimeline({stages}: {stages: InterviewProcessStep[]}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          observer.disconnect();
          stages.forEach((_, i) => {
            setTimeout(() => setVisibleCount((n) => Math.max(n, i + 1)), i * 150);
          });
        }
      },
      {threshold: 0.1},
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [stages]);

  return (
    <div className='mb-8' ref={containerRef}>
      {stages.map((stage, index) => (
        <div
          key={index}
          className='flex gap-4 transition-all duration-500'
          style={{
            opacity: index < visibleCount ? 1 : 0,
            transform: index < visibleCount ? 'translateY(0)' : 'translateY(16px)',
          }}>
          {/* Timeline column */}
          <div className='flex flex-col items-center -mt-1.5 max-[400]:mt-0'>
            <div className='w-9 h-9 rounded-full bg-iq-black text-white flex items-center justify-center text-sm font-bold shrink-0'>
              {index + 1}
            </div>
            {index < stages.length - 1 && (
              <div className='w-px flex-1 bg-iq-grey-light my-1 min-h-[24px]' />
            )}
          </div>

          {/* Content */}
          <div className={`flex-1 ${index < stages.length - 1 ? 'pb-8' : 'pb-0'}`}>
            <div className='flex items-baseline gap-x-2 mb-1 max-[400]:flex-col'>
              <h3 className='text-base font-bold text-iq-black'>{stage.title}</h3>
              <span className='text-xs text-iq-grey-medium'>{stage.subtitle}</span>
            </div>
            <p className='text-iq-grey-dark leading-relaxed'>{stage.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
